import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'

export interface ContactPerson {
  name: string
  role?: string
  email?: string
  phone?: string
  photo_url?: string
}

export interface ExhibitorDocument {
  name: string
  url: string
  type?: string
}

export interface ExhibitorProfile {
  id: string
  userId: string
  tenantId: string
  slug: string
  companyName: string | null
  companyDescription: string | null
  logoUrl: string | null
  websiteUrl: string | null
  contactPersons: ContactPerson[]
  documents: ExhibitorDocument[]
  personalMessage: string | null
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/[ß]/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50)
}

export function useExhibitorProfile() {
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useQuery({
    queryKey: ['exhibitor-profile', user?.id, activeWorkspace?.id],
    queryFn: async (): Promise<ExhibitorProfile | null> => {
      if (!user || !activeWorkspace) return null

      const { data, error } = await supabase
        .from('exhibitor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .eq('tenant_id', activeWorkspace.id)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      return {
        id: data.id,
        userId: data.user_id,
        tenantId: data.tenant_id,
        slug: data.slug,
        companyName: data.company_name,
        companyDescription: data.company_description,
        logoUrl: data.logo_url,
        websiteUrl: data.website_url,
        contactPersons: (data.contact_persons as ContactPerson[]) || [],
        documents: (data.documents as ExhibitorDocument[]) || [],
        personalMessage: data.personal_message,
      }
    },
    enabled: !!user && !!activeWorkspace,
  })
}

export function useSaveExhibitorProfile() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useMutation({
    mutationFn: async (profile: {
      companyName: string
      companyDescription?: string
      logoUrl?: string
      websiteUrl?: string
      contactPersons: ContactPerson[]
      documents: ExhibitorDocument[]
      personalMessage?: string
    }) => {
      if (!user || !activeWorkspace) throw new Error('Nicht angemeldet')

      const slug = slugify(profile.companyName || user.user_metadata?.name || user.email || 'aussteller')

      // Check if profile exists
      const { data: existing } = await supabase
        .from('exhibitor_profiles')
        .select('id')
        .eq('user_id', user.id)
        .eq('tenant_id', activeWorkspace.id)
        .maybeSingle()

      if (existing) {
        // Update
        const { error } = await supabase
          .from('exhibitor_profiles')
          .update({
            slug,
            company_name: profile.companyName,
            company_description: profile.companyDescription || null,
            logo_url: profile.logoUrl || null,
            website_url: profile.websiteUrl || null,
            contact_persons: profile.contactPersons,
            documents: profile.documents,
            personal_message: profile.personalMessage || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)

        if (error) throw error
      } else {
        // Insert
        const { error } = await supabase.from('exhibitor_profiles').insert({
          user_id: user.id,
          tenant_id: activeWorkspace.id,
          slug,
          company_name: profile.companyName,
          company_description: profile.companyDescription || null,
          logo_url: profile.logoUrl || null,
          website_url: profile.websiteUrl || null,
          contact_persons: profile.contactPersons,
          documents: profile.documents,
          personal_message: profile.personalMessage || null,
        })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exhibitor-profile'] })
    },
  })
}
